import { BellOff, BellRing, Siren } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, type AlertmanagerAlert } from '../../api';
import { Badge } from '../../components/Badge';
import { Callout, Field, FilterBar, Section, StatCard, StatGrid } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { alertLink, AlertRow, RefreshButton, SEVERITY_ORDER, SEVERITY_TONE, severityOf, useEnumLabel } from './shared';

type GroupBy = 'severity' | 'alertname' | 'service';
const GROUPS: GroupBy[] = ['severity', 'alertname', 'service'];

/** `/ops/alerts`: what is firing now, grouped, with incident creation (spec §117). */
export function AlertsPage() {
  const { t } = useTranslation('ops');
  const label = useEnumLabel();
  useDocumentTitle(t('alerts.title'));
  const result = useApi((signal) => api.listAlerts({ signal }), []);
  const [params, setParams] = useSearchParams();
  const groupBy: GroupBy = GROUPS.includes(params.get('group') as GroupBy) ? (params.get('group') as GroupBy) : 'severity';
  const showSuppressed = params.get('silenced') !== '0';
  const set = (k: string, v: string | null) => {
    const p = new URLSearchParams(params);
    if (v === null) p.delete(k);
    else p.set(k, v);
    setParams(p, { replace: true });
  };

  const everything = result.status === 'success' ? result.data.items : [];
  const alerts = everything.filter((a) => showSuppressed || a.status.state === 'active');
  const groups = new Map<string, AlertmanagerAlert[]>();
  for (const a of alerts) {
    const key = groupBy === 'severity' ? severityOf(a) : groupBy === 'alertname' ? (a.labels.alertname ?? '—') : (a.labels.service ?? '—');
    groups.set(key, [...(groups.get(key) ?? []), a]);
  }
  const order = (k: string) => {
    const i = (SEVERITY_ORDER as readonly string[]).indexOf(k);
    return i < 0 ? 99 : i;
  };
  const ordered = [...groups.entries()].sort(([a], [b]) => (groupBy === 'severity' ? order(a) - order(b) : a.localeCompare(b)));
  const active = everything.filter((a) => a.status.state === 'active');
  const urgent = active.filter((a) => ['P0', 'P1'].includes(severityOf(a))).length;

  /** The group heading: a severity badge, or a link to that alert / that service. */
  const heading = (key: string) => {
    if (groupBy === 'severity') {
      return key in SEVERITY_TONE ? (
        <Badge tone={SEVERITY_TONE[key]} title={key}>
          {key} · {label('severity', key)}
        </Badge>
      ) : (
        <code className="text-sm">{key}</code>
      );
    }
    if (key === '—') return <code className="text-sm">{key}</code>;
    const to = groupBy === 'alertname' ? alertLink(key) : `/ops/services/${encodeURIComponent(key)}`;
    return (
      <Link to={to} className="font-mono text-sm font-semibold text-primary hover:underline">
        {key}
      </Link>
    );
  };

  return (
    <>
      <PageHeader title={t('alerts.title')} subtitle={t('alerts.subtitle')} actions={<RefreshButton onClick={result.reload} busy={result.status === 'loading'} />} />
      {result.status === 'success' ? (
        <StatGrid className="mb-6 xl:grid-cols-3">
          <StatCard icon={BellRing} label={t('alerts.stat.firing')} value={formatCount(active.length)} tone={active.length ? 'warn' : 'ok'} />
          <StatCard icon={Siren} label={t('alerts.stat.urgent')} value={formatCount(urgent)} tone={urgent ? 'crit' : 'ok'} hint={t('alerts.stat.urgentHint')} />
          <StatCard icon={BellOff} label={t('alerts.stat.silenced')} value={formatCount(everything.length - active.length)} hint={t('alerts.stat.silencedHint')} />
        </StatGrid>
      ) : null}
      <FilterBar>
        <Field label={t('alerts.groupBy')} className="w-48">
          <Select value={groupBy} onValueChange={(v) => set('group', v === 'severity' ? null : v)}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GROUPS.map((g) => (
                <SelectItem key={g} value={g}>
                  {t(`alerts.group.${g}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <label className="flex h-9 items-center gap-2 text-sm">
          <Checkbox checked={showSuppressed} onCheckedChange={(v) => set('silenced', v === true ? null : '0')} />
          {t('alerts.showSilenced')}
        </label>
      </FilterBar>
      <Callout className="mb-6">{t('alerts.readOnly')}</Callout>

      {result.status === 'loading' ? <Loading rows={3} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('alerts.loadFailed')} /> : null}
      {result.status === 'success' && alerts.length === 0 ? <EmptyState title={t('alerts.emptyTitle')}>{t('alerts.emptyBody')}</EmptyState> : null}
      <div className="space-y-4">
        {ordered.map(([key, list]) => (
          <Section
            key={key}
            flush
            title={
              <span className="flex items-center gap-2">
                {heading(key)}
                <span className="text-sm font-normal text-muted-foreground">{list.length}</span>
              </span>
            }
          >
            <ul className="divide-y border-t">
              {list.map((a) => (
                <AlertRow key={a.fingerprint} a={a} />
              ))}
            </ul>
          </Section>
        ))}
      </div>
    </>
  );
}
